import { useQueries, useQuery, type UseQueryResult } from '@tanstack/react-query';
import type {
  PublicBracket,
  PublicClubCompetitions,
  PublicCompetitionInfo,
  PublicCourtSchedule,
  PublicMatches,
  PublicStandings,
} from '../model/publicTypes';
import {
  getPublicBracket,
  getPublicClubCompetitions,
  getPublicCompetitionInfo,
  getPublicCourtSchedule,
  getPublicMatches,
  getPublicStandings,
} from './publicApi';

/**
 * Co ile ekran prezentacji sam sie odswieza. Ekran wisi w klubie bez obslugi,
 * wiec dane musza dojezdzac same — nikt nie nacisnie F5.
 */
export const PUBLIC_SCREEN_REFRESH_MS = 30_000;

/** Klucze pamieci podrecznej trybu prezentacji — rozlaczne z widokami klubowymi. */
export const publicScreenKeys = {
  all: ['public-screen'] as const,
  clubCompetitions: (clubId: string) => [...publicScreenKeys.all, 'club', clubId] as const,
  info: (competitionId: string) => [...publicScreenKeys.all, 'info', competitionId] as const,
  bracket: (competitionId: string) => [...publicScreenKeys.all, 'bracket', competitionId] as const,
  matches: (competitionId: string) => [...publicScreenKeys.all, 'matches', competitionId] as const,
  standings: (competitionId: string) =>
    [...publicScreenKeys.all, 'standings', competitionId] as const,
  courtSchedule: (competitionId: string, date: string) =>
    [...publicScreenKeys.all, 'courts', competitionId, date] as const,
};

/**
 * Wspolne ustawienia zapytan ekranu: odswiezanie takze wtedy, gdy karta jest
 * w tle (`refetchIntervalInBackground`) — telewizor w klubie nigdy nie jest
 * „aktywna zakladka”, a mimo to ma pokazywac biezacy stan.
 */
const SCREEN_QUERY_OPTIONS = {
  refetchInterval: PUBLIC_SCREEN_REFRESH_MS,
  refetchIntervalInBackground: true,
} as const;

/** Pobiera publiczne rozgrywki klubu — lista do przelacznika turniejow. */
export function usePublicClubCompetitionsQuery(
  clubId: string,
): UseQueryResult<PublicClubCompetitions> {
  return useQuery({
    queryKey: publicScreenKeys.clubCompetitions(clubId),
    queryFn: () => getPublicClubCompetitions(clubId),
    ...SCREEN_QUERY_OPTIONS,
  });
}

/** Pobiera podsumowanie wybranej rozgrywki. */
export function usePublicCompetitionInfoQuery(
  competitionId: string | undefined,
): UseQueryResult<PublicCompetitionInfo> {
  return useQuery({
    queryKey: publicScreenKeys.info(competitionId ?? ''),
    queryFn: () => getPublicCompetitionInfo(competitionId ?? ''),
    enabled: Boolean(competitionId),
    ...SCREEN_QUERY_OPTIONS,
  });
}

/**
 * Pobiera drabinke. Wlaczana dopiero, gdy rozgrywka ma `bracketSize` —
 * przed losowaniem backend odpowiada 409, a nie pusta drabinka.
 */
export function usePublicBracketQuery(
  competitionId: string | undefined,
  enabled: boolean,
): UseQueryResult<PublicBracket> {
  return useQuery({
    queryKey: publicScreenKeys.bracket(competitionId ?? ''),
    queryFn: () => getPublicBracket(competitionId ?? ''),
    enabled: Boolean(competitionId) && enabled,
    ...SCREEN_QUERY_OPTIONS,
  });
}

/** Pobiera wszystkie mecze rozgrywki — zrodlo sekcji live / nastepne / wyniki. */
export function usePublicMatchesQuery(
  competitionId: string | undefined,
): UseQueryResult<PublicMatches> {
  return useQuery({
    queryKey: publicScreenKeys.matches(competitionId ?? ''),
    queryFn: () => getPublicMatches(competitionId ?? ''),
    enabled: Boolean(competitionId),
    ...SCREEN_QUERY_OPTIONS,
  });
}

/**
 * Pobiera tabele grup — zrodlo sekcji fazy grupowej. Wlaczana dopiero po
 * losowaniu: przed nim tabel nie ma i zapytanie wracaloby z pusta lista
 * co 30 sekund.
 */
export function usePublicStandingsQuery(
  competitionId: string | undefined,
  enabled: boolean,
): UseQueryResult<PublicStandings> {
  return useQuery({
    queryKey: publicScreenKeys.standings(competitionId ?? ''),
    queryFn: () => getPublicStandings(competitionId ?? ''),
    enabled: Boolean(competitionId) && enabled,
    ...SCREEN_QUERY_OPTIONS,
  });
}

/**
 * Wynik zapytania zbiorczego zakladki „Info ogólne": odpowiedzi ulozone
 * po identyfikatorze rozgrywki plus zbiorcze flagi stanu.
 */
export interface PublicBatchResult<T> {
  /** Odpowiedzi, ktore juz dojechaly — klucz to id rozgrywki. */
  byId: Map<string, T>;
  /** `true`, dopoki ktorekolwiek zapytanie jeszcze trwa. */
  isPending: boolean;
  /** `true`, gdy ktorekolwiek zapytanie zakonczylo sie bledem. */
  isError: boolean;
}

/** Sklejenie wynikow `useQueries` w mape po id rozgrywki. */
function combineById<T>(ids: readonly string[]) {
  return (results: { data: T | undefined; isPending: boolean; isError: boolean }[]) => {
    const byId = new Map<string, T>();
    results.forEach((result, index) => {
      if (result.data !== undefined) byId.set(ids[index]!, result.data);
    });
    return {
      byId,
      isPending: results.some((result) => result.isPending),
      isError: results.some((result) => result.isError),
    };
  };
}

/** Mecze wielu rozgrywek naraz — zrodlo sekcji zbiorczych zakladki „Info ogólne". */
export function usePublicMatchesBatchQuery(
  ids: readonly string[],
): PublicBatchResult<PublicMatches> {
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: publicScreenKeys.matches(id),
      queryFn: () => getPublicMatches(id),
      ...SCREEN_QUERY_OPTIONS,
    })),
    combine: combineById<PublicMatches>(ids),
  });
}

/** Plany kortow wielu rozgrywek na jeden dzien — zrodlo osi czasu imprezy. */
export function usePublicCourtSchedulesBatchQuery(
  ids: readonly string[],
  date: string,
): PublicBatchResult<PublicCourtSchedule> {
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: publicScreenKeys.courtSchedule(id, date),
      queryFn: () => getPublicCourtSchedule(id, date),
      ...SCREEN_QUERY_OPTIONS,
    })),
    combine: combineById<PublicCourtSchedule>(ids),
  });
}

/**
 * Drabinki wielu rozgrywek — zakladka „Info ogólne" pobiera je tylko dla
 * turniejow zakonczonych, zeby pokazac mistrzow na kartach.
 */
export function usePublicBracketsBatchQuery(
  ids: readonly string[],
): PublicBatchResult<PublicBracket> {
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: publicScreenKeys.bracket(id),
      queryFn: () => getPublicBracket(id),
      ...SCREEN_QUERY_OPTIONS,
    })),
    combine: combineById<PublicBracket>(ids),
  });
}

/**
 * Tabele wielu rozgrywek — mistrzowie zakonczonych turniejow bez drabinki
 * (grupa finalowa, grupy klasyfikacyjne, liga) wynikaja z tabel, nie z drabinki.
 */
export function usePublicStandingsBatchQuery(
  ids: readonly string[],
): PublicBatchResult<PublicStandings> {
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: publicScreenKeys.standings(id),
      queryFn: () => getPublicStandings(id),
      ...SCREEN_QUERY_OPTIONS,
    })),
    combine: combineById<PublicStandings>(ids),
  });
}

/** Pobiera plan kortow na wskazany dzien — sekcja „Status kortow”. */
export function usePublicCourtScheduleQuery(
  competitionId: string | undefined,
  date: string,
): UseQueryResult<PublicCourtSchedule> {
  return useQuery({
    queryKey: publicScreenKeys.courtSchedule(competitionId ?? '', date),
    queryFn: () => getPublicCourtSchedule(competitionId ?? '', date),
    enabled: Boolean(competitionId),
    ...SCREEN_QUERY_OPTIONS,
  });
}
